import type { ApiClient } from "@/modules/api/apiClient.ts";
import { showAndReportError } from "@/modules/error/errorStream.ts";
import { paths } from "@warrenio/api-spec/paths";
import { adminPaths } from "@warrenio/api-spec/paths.admin";
import { deepStrict } from "@warrenio/api-spec/strict";
import { isObject } from "@warrenio/utils/isObject";
import { z } from "zod";
import type {
    ZodOpenApiOperationObject,
    ZodOpenApiPathItemObject,
    ZodOpenApiPathsObject,
    ZodOpenApiResponseObject,
    ZodOpenApiResponsesObject,
} from "zod-openapi";

/**
 * API paths for which to check schema conformance.
 *
 * In dev mode, also check admin paths.
 * In production, only check user-facing endpoints to reduce bundle size and not leak admin documentation.
 */
const checkPaths: ZodOpenApiPathsObject = import.meta.env.DEV ? { ...paths, ...adminPaths } : paths;

/** Automatically check that the response data matches the Zod API spec's declared schema. */
export function registerSchemaCheckMiddleware(client: ApiClient) {
    if (!checkPaths) {
        return;
    }

    client.use({
        async onResponse({ request: { method }, schemaPath, response }) {
            const schema = getSchemaForRequest(schemaPath, method, response);
            if (schema) {
                let data: unknown;

                try {
                    // Clone the response so we can read it twice
                    data = await response.clone().json();
                } catch (e) {
                    console.error("Failed to parse JSON response for schema check", e);
                    return response;
                }

                validateSchema(response, schema, data);
            }

            return response;
        },
    });
}

/** Get the Zod schema for an OpenAPI endpoint. */
function getSchemaForRequest(schemaPath: string, method: string, response: Response): z.ZodTypeAny | undefined {
    const pathInfo: ZodOpenApiPathItemObject | undefined = checkPaths[schemaPath];
    const methodInfo = pathInfo?.[method.toLowerCase() as keyof ZodOpenApiPathItemObject] as
        | ZodOpenApiOperationObject
        | undefined;
    const responseInfo = methodInfo?.responses[String(response.status) as keyof ZodOpenApiResponsesObject] as
        | ZodOpenApiResponseObject
        | undefined;

    const responseContentType = response.headers.get("content-type");
    if (responseContentType && !responseContentType.includes("application/json")) {
        console.warn("Skipping schema check for non-JSON response: %s @ %s", responseContentType, response.url);
        return;
    }
    const contentType = "application/json";

    const schema = responseInfo?.content?.[contentType]?.schema;

    // console.debug({ pathInfo, methodInfo, responseInfo, schema });

    if (schema == null) {
        console.warn("No Zod schema found for %s[%s][%s]", schemaPath, method, response.status);
    } else if (!(schema instanceof z.ZodType)) {
        console.warn("Invalid Zod type for %s[%s][%s]: %o", schemaPath, method, response.status, schema);
    } else {
        return schema;
    }
}

function getSchemaTypeName(schema: z.ZodTypeAny): string | undefined {
    if (schema instanceof z.ZodArray) {
        const elType = getSchemaTypeName((schema as z.ZodArray<z.ZodTypeAny>).element);
        return elType ? `${elType}[]` : undefined;
    }

    const _def: unknown = schema._def;
    if (!isObject(_def)) return;
    const openapi: unknown = _def.openapi;
    if (!isObject(openapi)) return;
    const ref: unknown = openapi.ref;
    return typeof ref === "string" ? ref : undefined;
}

export function validateSchema(response: { url: string }, schema: z.ZodTypeAny, data: unknown) {
    schema = deepStrict(schema);
    try {
        schema.parse(data);
    } catch (e) {
        const typeName = getSchemaTypeName(schema) ?? "<unknown>";
        showAndReportError(
            // Group by schema name in error reporting
            { component: typeName },
            "Failed to validate API response from:\n%s\nExpected type: %s\n📎 Data:\n%o\n🛑 Error:\n%o",
            response.url,
            typeName,
            data,
            e,
        );
    }
}
